import { useState, useEffect, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth-context";
import { isStudioPlan } from "@/lib/plan";
import { useGetCreditBalance } from "@workspace/api-client-react";
import {
  creditWarningLevel,
  creditWarningPillText,
} from "@/lib/credit-warning";
import { LanguageSwitcher } from "@/components/language-switcher";
import { SubscriptionExpiryReminder } from "@/components/subscription-expiry-reminder";
import { CreditWarningReminder } from "@/components/credit-warning-reminder";
import { canAccessTeam } from "@/lib/team-access";
import { AiProcessingIndicator } from "@/components/ai-processing-indicator";
import { useAiProcessingIndicatorState } from "@/lib/ai-processing-state";
import { NotificationsBell } from "@/components/notifications-bell";

const ICON_STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function IcoDashboard() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </svg>
  );
}
function IcoAssets() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}
function IcoCreative() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M12 19l7-7 3 3-7 7-3-3z" />
      <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
      <path d="M2 2l7.586 7.586" />
      <circle cx="11" cy="11" r="2" />
    </svg>
  );
}
function IcoVideo() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="M22 8l-6 4 6 4V8z" />
    </svg>
  );
}
function IcoPresets() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}
function IcoJobs() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}
function IcoSettings() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function IcoCredits() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </svg>
  );
}
function IcoReferrals() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="9" cy="7" r="4" />
      <path d="M2 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="16" y1="11" x2="22" y2="11" />
    </svg>
  );
}
function IcoAffiliate() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
function IcoLocation() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IcoIngest() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}
function IcoCampaigns() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="1" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="1" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="23" y2="12" />
    </svg>
  );
}
function IcoTelemetry() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" {...ICON_STROKE}>
      <path d="M3 3v18h18" />
      <path d="M7 14l3-3 3 2 4-5" />
    </svg>
  );
}
function IcoCart() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  );
}
function IcoBilling() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  );
}
function IcoTeam() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M17 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M9 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IcoReview() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}
interface NavItem {
  nameKey: string;
  href: string;
  icon: ReactNode;
}

const MAIN_NAV: NavItem[] = [
  { nameKey: "nav.dashboard", href: "/dashboard", icon: <IcoDashboard /> },
  { nameKey: "nav.assets", href: "/assets", icon: <IcoAssets /> },
  { nameKey: "nav.creativeSpace", href: "/creative-space", icon: <IcoCreative /> },
  { nameKey: "nav.bannerToVideo", href: "/banner-to-video", icon: <IcoVideo /> },
];

const SECTION_LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "1.4px",
  color: "#8C7E70",
  padding: "16px 8px 6px",
  fontWeight: 600,
};

const NAV_ACTIVE_STYLE: React.CSSProperties = {
  background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
  color: "#fff",
  boxShadow: "0 10px 22px rgba(255,89,0,.35),inset 0 1px 0 rgba(255,255,255,.18)",
};

export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(["app", "common"]);
  const [location, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<"credit" | "settings" | null>(null);
  const [closingPanel, setClosingPanel] = useState<"credit" | "settings" | null>(null);
  const aiProcessingState = useAiProcessingIndicatorState(location);
  const displayedPanel = activePanel ?? closingPanel;

  const closeActivePanel = () => {
    if (!activePanel) return;
    setClosingPanel(activePanel);
    setActivePanel(null);
  };

  useEffect(() => {
    setDrawerOpen(false);
    if (window.innerWidth < 1024) setActivePanel(null);
  }, [location]);

  const handleLogout = async () => {
    await logout();
    setLocation("/");
  };

  const { data: balance } = useGetCreditBalance({
    query: { enabled: !!user } as never,
  });

  const isActive = (href: string) => location === href || location.startsWith(href + "/");

  // Studio-plan accounts get the Adaptive Location entry (server re-checks the
  // plan on every request; this only controls visibility).
  const mainNav: NavItem[] = user?.isReviewer
    ? [{ nameKey: "nav.reviews", href: "/reviews", icon: <IcoReview /> }]
    : isStudioPlan(user)
      ? [
        ...MAIN_NAV,
        {
          nameKey: "nav.adaptiveLocation",
          href: "/adaptive-location",
          icon: <IcoLocation />,
        },
      ]
      : MAIN_NAV;

  // Studio-plan (non-reviewer) accounts get the data-processing tools (ingest +
  // campaign matching) in their own section; gating mirrors the Adaptive
  // Location entry above (server re-checks the plan on every request).
  const dataProcessingNav: NavItem[] =
    !user?.isReviewer && isStudioPlan(user)
      ? [
        { nameKey: "nav.ingest", href: "/data-processing/ingest", icon: <IcoIngest /> },
        { nameKey: "nav.campaigns", href: "/data-processing/campaigns", icon: <IcoCampaigns /> },
      ]
      : [];

  const creditNav: NavItem[] = [
    { nameKey: "nav.purchase", href: "/purchase", icon: <IcoCart /> },
    { nameKey: "nav.billing", href: "/billing", icon: <IcoBilling /> },
    { nameKey: "nav.credits", href: "/credits", icon: <IcoCredits /> },
  ];
  const appSettingsNav: NavItem[] = [
    { nameKey: "nav.generalSettings", href: "/settings", icon: <IcoSettings /> },
    { nameKey: "nav.sizeLibrary", href: "/presets", icon: <IcoPresets /> },
    { nameKey: "nav.jobHistory", href: "/jobs", icon: <IcoJobs /> },
  ];
  const accountSettingsNav: NavItem[] = [
    { nameKey: "nav.referrals", href: "/referrals", icon: <IcoReferrals /> },
    ...(user?.isAffiliate
      ? [{ nameKey: "nav.affiliate", href: "/affiliate", icon: <IcoAffiliate /> }]
      : []),
    ...(canAccessTeam(user?.accountRole)
      ? [{ nameKey: "nav.team", href: "/team", icon: <IcoTeam /> }]
      : []),
  ];

  const initial = (user?.displayName || user?.username || "?").charAt(0).toUpperCase();

  const renderNavItem = (item: NavItem, muted = false) => {
    const active = isActive(item.href);
    return (
      <Link key={item.nameKey} href={item.href} asChild>
        <a
          data-testid={`link-nav-${item.href.replace(/\//g, "-").replace(/^-/, "")}`}
          aria-current={active ? "page" : undefined}
          className="nav-row group"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "9px 12px",
            borderRadius: 11,
            fontSize: 13.5,
            color: muted ? "#9A8A7C" : "#F3ECE3",
            marginBottom: 2,
            cursor: "pointer",
            transition: ".15s",
            ...(active ? NAV_ACTIVE_STYLE : {}),
          }}
        >
          {item.icon}
          {t(item.nameKey)}
        </a>
      </Link>
    );
  };

  const renderPanelToggle = (
    panel: "credit" | "settings",
    labelKey: string,
    icon: ReactNode,
  ) => {
    const expanded = activePanel === panel;
    const containsCurrentRoute =
      panel === "credit"
        ? creditNav.some((item) => isActive(item.href)) || isActive("/checkout")
        : [...appSettingsNav, ...accountSettingsNav].some((item) =>
          isActive(item.href),
        );
    const highlighted = expanded || (activePanel === null && containsCurrentRoute);
    return (
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`sidebar-panel-${panel}`}
        onClick={() => {
          setClosingPanel(null);
          setActivePanel(panel);
        }}
        className="nav-row group w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFB47A]"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "10px 12px",
          borderRadius: 11,
          border: "none",
          fontSize: 13.5,
          color: "#F3ECE3",
          marginBottom: 3,
          cursor: "pointer",
          transition: ".2s",
          ...(highlighted ? NAV_ACTIVE_STYLE : {}),
        }}
      >
        {icon}
        {t(labelKey)}
      </button>
    );
  };

  return (
    <div
      className="app-grid grid h-screen"
      style={{
        gridTemplateColumns: displayedPanel
          ? "240px 300px minmax(0,1fr)"
          : "240px minmax(0,1fr)",
        transition: "grid-template-columns .25s ease",
      }}
    >
      {/* backdrop (mobile) */}
      {drawerOpen && (
        <button
          type="button"
          aria-label={t("shell.closeMenu")}
          onClick={() => {
            setDrawerOpen(false);
            // closeActivePanel();
          }}
          className="fixed inset-0 z-[150] lg:hidden"
          style={{ background: "rgba(0,0,0,.55)", backdropFilter: "blur(2px)" }}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`navside flex flex-col ${drawerOpen ? "open" : ""}`}
        onClickCapture={(event) => {
          const action = (event.target as Element).closest("a, button");
          if (action && !action.matches('[aria-controls^="sidebar-panel-"]')) {
            closeActivePanel();
          }
        }}
        style={{
          borderRight: "1px solid rgba(255,255,255,.1)",
          padding: "18px 0 12px",
          background: "linear-gradient(180deg,rgba(56,42,32,.4),rgba(34,26,20,.2))",
          backdropFilter: "blur(12px)",
          minHeight: 0,
        }}
      >
        {/* logo */}
        <div
          className="flex items-center"
          style={{
            padding: "0 18px 16px",
            gap: 11,
            borderBottom: "1px solid rgba(255,255,255,.1)",
            marginBottom: 10,
          }}
        >
          <Link to="/" aria-label={t("shell.home")} asChild>
            <img
              src="/logo.png"
              alt="Creative Adapt"
              style={{ height: 56, width: "auto", objectFit: "contain", cursor: "pointer" }}
            />
          </Link>

        </div>

        {/* scrollable nav */}
        <div className="no-scrollbar flex-1 overflow-y-auto" style={{ minHeight: 0, padding: "0 12px" }}>
          <div style={{ ...SECTION_LABEL_STYLE, paddingTop: 12 }}>
            {t("nav.section.main")}
          </div>
          {mainNav.map((item) => renderNavItem(item))}

          {dataProcessingNav.length > 0 && (
            <>
              <div style={SECTION_LABEL_STYLE}>{t("nav.section.dataProcessing")}</div>
              {dataProcessingNav.map((item) => renderNavItem(item))}
            </>
          )}

          <div style={SECTION_LABEL_STYLE}>{t("nav.section.accountBilling")}</div>
          {renderPanelToggle("credit", "nav.creditMenu", <IcoCredits />)}
          {renderPanelToggle("settings", "nav.generalSettings", <IcoSettings />)}

          {/* {user && (
            <>
              <div style={SECTION_LABEL_STYLE}>{t("nav.section.internal")}</div>
              {renderNavItem(
                { nameKey: "nav.telemetry", href: "/telemetry/bbox-crop", icon: <IcoTelemetry /> },
                true,
              )}
            </>
          )} */}
        </div>

        {/* footer user */}
        <div style={{ flex: "none", padding: "8px 12px 0" }}>
          <div style={{ height: 1, background: "rgba(255,255,255,.1)", margin: "6px 2px 10px" }} />
          {balance && (() => {
            const level = creditWarningLevel(balance.total);
            const warnText = creditWarningPillText(level);
            const palette =
              level === "empty"
                ? { border: "rgba(255,90,90,.45)", bg: "rgba(255,80,80,.12)", icon: "#FF7A7A", warn: "#FF9A9A" }
                : level === "low"
                  ? { border: "rgba(255,180,60,.45)", bg: "rgba(255,180,60,.12)", icon: "#FFC15A", warn: "#FFD080" }
                  : { border: "rgba(255,138,50,.28)", bg: "rgba(255,138,50,.1)", icon: "#FF8A4B", warn: "#8C7E70" };
            return (
              <Link href="/credits" asChild>
                <a
                  data-testid="link-credit-balance"
                  data-credit-level={level}
                  className="flex items-center"
                  style={{
                    gap: 10,
                    padding: "8px 11px",
                    marginBottom: 8,
                    borderRadius: 11,
                    border: `1px solid ${palette.border}`,
                    background: palette.bg,
                    cursor: "pointer",
                    transition: ".15s",
                  }}
                >
                  <span style={{ color: palette.icon, flex: "none", display: "flex" }}>
                    {warnText ? (
                      <svg viewBox="0 0 24 24" width="17" height="17" {...ICON_STROKE}>
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    ) : (
                      <IcoCredits />
                    )}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#F3ECE3" }}
                      data-testid="text-credit-total"
                    >
                      {balance.total.toLocaleString("vi-VN")}
                    </span>
                    <span
                      style={{ display: "block", fontSize: 10.5, color: palette.warn }}
                      data-testid="text-credit-warning"
                    >
                      {warnText ?? t("shell.creditsAvailable")}
                    </span>
                  </span>
                </a>
              </Link>
            );
          })()}
          <div style={{ padding: "0 6px 8px" }}>
            <LanguageSwitcher className="text-[#F3ECE3]" />
          </div>
          <div className="flex items-center" style={{ gap: 10, padding: "4px 6px" }}>
            <div
              className="flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
                flex: "none",
                boxShadow: "0 5px 12px rgba(255,89,0,.32)",
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
              }}
            >
              {initial}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                data-testid="text-user-name"
              >
                {user?.displayName ?? t("shell.guest")}
              </div>
              <div style={{ fontSize: 11, color: "#8C7E70", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {user?.username ? `@${user.username}` : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title={t("shell.logout")}
              data-testid="button-logout"
              className="logout-btn flex items-center justify-center"
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                border: "1px solid rgba(255,255,255,.12)",
                background: "rgba(255,255,255,.05)",
                flex: "none",
                color: "#8C7E70",
                cursor: "pointer",
                transition: ".15s",
              }}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {displayedPanel && (
        <aside
          key={`${displayedPanel}-${activePanel ? "open" : "closing"}`}
          id={`sidebar-panel-${displayedPanel}`}
          className="sidebar-subpanel no-scrollbar overflow-y-auto"
          onAnimationEnd={(event) => {
            if (event.animationName === "sidebar-panel-out" && !activePanel) {
              setClosingPanel(null);
            }
          }}
          style={{
            minHeight: 0,
            borderRight: "1px solid rgba(255,255,255,.1)",
            background: "linear-gradient(180deg,rgba(29,22,17,.98),rgba(12,9,7,.98))",
            animation: activePanel
              ? "sidebar-panel-in .25s ease both"
              : "sidebar-panel-out .2s ease both",
            pointerEvents: activePanel ? "auto" : "none",
          }}
        >
          <div
            style={{
              height: 92,
              display: "flex",
              alignItems: "center",
              padding: "0 20px",
              borderBottom: "1px solid rgba(255,255,255,.1)",
              fontSize: 17,
              fontWeight: 500,
              textTransform: "uppercase",
              color: "#F3ECE3",
            }}
          >
            {t(displayedPanel === "credit" ? "nav.creditMenu" : "nav.generalSettings")}
          </div>

          <div
            style={{ padding: "18px 12px" }}
            onClick={(event) => {
              // if (event.target instanceof Element && event.target.closest("a, button")) {
              //   closeActivePanel();
              // }
            }}
          >
            {displayedPanel === "credit" ? (
              creditNav.map((item) => renderNavItem(item))
            ) : (
              <>
                <div style={{ ...SECTION_LABEL_STYLE, paddingTop: 0 }}>
                  {t("nav.section.application")}
                </div>
                {appSettingsNav.map((item) => renderNavItem(item))}

                <div
                  style={{
                    height: 1,
                    background: "rgba(255,255,255,.1)",
                    margin: "16px -12px 6px",
                  }}
                />
                <div style={SECTION_LABEL_STYLE}>{t("nav.section.account")}</div>
                <NotificationsBell variant="menu" />
                {accountSettingsNav.map((item) => renderNavItem(item))}

                <div
                  style={{
                    height: 1,
                    background: "rgba(255,255,255,.1)",
                    margin: "16px -12px 10px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleLogout}
                  className="nav-row w-full"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 11,
                    color: "#F3ECE3",
                    textAlign: "left",
                    fontSize: 13.5,
                    cursor: "pointer",
                  }}
                >
                  {t("shell.logout")}
                </button>
              </>
            )}
          </div>
        </aside>
      )}

      {/* MAIN */}
      <main className="flex flex-col" style={{ minHeight: 0, position: "relative" }}>
        {aiProcessingState && (
          <AiProcessingIndicator
            variant="floating"
            title={t("common:aiProcessing.title")}
            description=""
            detail={aiProcessingState.detail}
            progress={aiProcessingState.progress}
            onClick={() => setLocation(aiProcessingState.sourcePath)}
          />
        )}
        {/* mobile top bar */}
        <div
          className="mtop flex items-center lg:hidden"
          style={{
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(255,255,255,.08)",
            background: "linear-gradient(160deg,rgba(30,25,21,.94),rgba(16,13,11,.9))",
            flex: "none",
            position: "relative",
            zIndex: 60,
          }}
        >
          <button
            type="button"
            aria-label={t("shell.openMenu")}
            onClick={() => setDrawerOpen(true)}
            className="flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(255,255,255,.05)",
              cursor: "pointer",
              color: "#F3ECE3",
              flex: "none",
            }}
          >
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="flex items-center" style={{ gap: 9, flex: 1, minWidth: 0 }}>
            <img
              src="/app-icon.png"
              alt="Creative Adapt"
              style={{ width: 38, height: 38, borderRadius: 10, objectFit: "cover", flex: "none" }}
            />
          </div>
          <LanguageSwitcher className="text-[#F3ECE3]" />
          <Link href="/create/upload" asChild>
            <a
              aria-label={t("shell.createNewDesign")}
              className="flex items-center justify-center"
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: "linear-gradient(135deg,#FF8A2B,#FF5900)",
                cursor: "pointer",
                flex: "none",
                boxShadow: "0 8px 18px rgba(255,89,0,.4)",
                color: "#fff",
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fff" strokeWidth={2.4} strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </a>
          </Link>
        </div>

        <div
          className="no-scrollbar scroll-pad flex-1 overflow-y-auto px-[15px] pt-[14px] pb-20 lg:px-[34px] lg:py-[26px]"
          style={{ minHeight: 0 }}
        >
          <SubscriptionExpiryReminder />
          <CreditWarningReminder />
          {children}
        </div>
      </main>
    </div>
  );
}
